import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import { getManagementTaskSummary } from '../../services/api/implementation/impl';

const formatCount = (n) => String(n ?? 0).padStart(2, '0');

const formatDueDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const SummaryStat = ({ label, value, highlight }) => (
  <div className="text-center">
    <p
      className={`text-lg font-bold leading-none ${
        highlight ? 'text-[#0f2a45]' : 'text-gray-700'
      }`}>
      {formatCount(value)}
    </p>
    <p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
      {label}
    </p>
  </div>
);

const UserTaskCard = ({ user, expanded, onToggle }) => (
  <div className="rounded-xl border border-gray-200 bg-white">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-3 p-4 text-left">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[#0f2a45]">{user.name}</p>
        <p className="text-xs text-gray-500">{user.role || 'Assignee'}</p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <div className="text-right">
          <p className="text-xs font-semibold text-gray-600">
            {user.completed}/{user.total}
          </p>
          <div className="mt-1 h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full ${user.barColor || 'bg-amber-400'}`}
              style={{ width: `${user.percent || 0}%` }}
            />
          </div>
          <p className="mt-0.5 text-[10px] text-gray-400">{user.percent || 0}%</p>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </div>
    </button>

    {expanded && user.tasks?.length > 0 && (
      <div className="space-y-4 border-t border-gray-100 px-4 pb-4 pt-3">
        {user.tasks.map((task) => (
          <div key={task.id} className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-[#0f2a45]">{task.title}</p>
              <span className="shrink-0 rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                {task.status || 'Open'}
              </span>
            </div>
            {task.dueDate ? (
              <p className="mt-1 text-xs text-gray-500">Due {formatDueDate(task.dueDate)}</p>
            ) : null}
          </div>
        ))}
      </div>
    )}
  </div>
);

const TaskAccountabilityPanel = ({ projectID = null }) => {
  const [expandedUsers, setExpandedUsers] = useState({});
  const hasProject = projectID !== null && projectID !== undefined && String(projectID) !== '';

  const { data, isPending, isError } = useQuery({
    queryKey: ['managementTaskSummary', hasProject ? Number(projectID) : 'all'],
    queryFn: () => getManagementTaskSummary(hasProject ? projectID : undefined),
  });

  const summary = data?.data || {
    pending: 0,
    delayed: 0,
    done: 0,
    total: 0,
    assignees: []
  };
  const users = summary.assignees || [];

  const defaultExpanded = useMemo(() => {
    const first = users[0]?.id;
    return first ? { [first]: true } : {};
  }, [users]);

  const expanded = Object.keys(expandedUsers).length ? expandedUsers : defaultExpanded;

  const toggleUser = (id) => {
    setExpandedUsers((prev) => {
      const current = Object.keys(prev).length ? prev : defaultExpanded;
      return { ...current, [id]: !current[id] };
    });
  };

  if (isPending) {
    return (
      <div className="flex h-full w-full min-w-0 flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="py-12 text-center text-sm text-gray-500">Loading accountability...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full w-full min-w-0 flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className="py-12 text-center text-sm text-rose-600">
          Unable to load task accountability.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full min-w-0 flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Users className="h-4 w-4 text-[#1E4A53]" />
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#1E4A53]">
          Task Accountability
        </h3>
      </div>

      <div className="mb-5 grid grid-cols-4 gap-2 rounded-xl border border-gray-100 bg-gray-50 p-3">
        <SummaryStat label="Pending" value={summary.pending} />
        <SummaryStat label="Overdue" value={summary.delayed || summary.overdue} highlight />
        <SummaryStat label="Done" value={summary.done} />
        <SummaryStat label="Total" value={summary.total} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {users.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            No management tasks yet for this project.
          </p>
        ) : (
          users.map((user) => (
            <UserTaskCard
              key={user.id}
              user={user}
              expanded={Boolean(expanded[user.id])}
              onToggle={() => toggleUser(user.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default TaskAccountabilityPanel;
